import React, { useId } from 'react';
import clsx from 'clsx';
import type { FieldValues } from 'react-hook-form';
import { Input as KendoInput } from '@progress/kendo-react-inputs';
import { Label, Error, Hint } from '@progress/kendo-react-labels';
import { Button } from '../Button/Button';
import type { InputProps } from './Input.types';
import styles from './Input.module.css';

export function Input<TFieldValues extends FieldValues = FieldValues>({
  id: idProp,
  label,
  placeholder,
  maxLength,
  validationMessage,
  helpMessage,
  className = '',
  disabled = false,
  leftIcon,
  rightIcon,
  rightIconOnClick,
  rightIconAriaLabel,
  rightButton,
  type = 'text',
  inputMode,
  field,
  onKeyDown,
}: InputProps<TFieldValues>): React.ReactElement {
  const autoId = useId();
  const id = idProp ?? autoId;

  const hasError = !!validationMessage;
  const hintId = !hasError && helpMessage ? `${id}-hint` : undefined;
  const errorId = hasError ? `${id}-error` : undefined;
  const describedBy = errorId ?? hintId;

  const outerClass = clsx(
    styles.input,
    leftIcon && styles.withLeftIcon,
    rightIcon && styles.withRightIcon,
    rightIcon && rightIconOnClick && styles.withRightBtn,
    className
  );

  return (
    <div className={outerClass}>
      {label && (
        <Label className={clsx('p1', 'primary-text-color-main-normal')} editorId={id} editorValid={!hasError} editorDisabled={disabled}>
          {label}
        </Label>
      )}

      <div className={clsx(styles.controlRow, 'gap-16')}>
        <div className={styles.field}>
          {leftIcon && (
            <span className={`${styles.iconLeft}`} aria-hidden="true">
              {leftIcon}
            </span>
          )}

          <KendoInput
            id={id}
            type={type}
            placeholder={placeholder}
            maxLength={maxLength}
            disabled={disabled}
            valid={!hasError}
            aria-describedby={describedBy}
            className={clsx('p1 book', 'text-color-placeholder')}
            inputMode={inputMode}
            {...field}
            onKeyDown={onKeyDown}
          />

          {rightIcon && rightIconOnClick && !disabled ? (
            <button type="button" className={`${styles.rightIcon} icon`} onClick={rightIconOnClick} aria-label={rightIconAriaLabel}>
              {rightIcon}
            </button>
          ) : (
            rightIcon && (
              <span className={`${styles.iconRight}`} aria-hidden="true">
                {rightIcon}
              </span>
            )
          )}
        </div>

        {rightButton ? (
          <Button
            type="button"
            styleType="filled"
            variant="primary"
            className={clsx(styles.rightButton, 'p-tb-plus1', 'p-lr')}
            onClick={rightButton.onClick}
            ariaLabel={rightButton.ariaLabel ?? 'Field action'}
            disabled={disabled || rightButton.disabled}
            iconOnly
          >
            {rightButton.icon}
          </Button>
        ) : null}
      </div>

      <div className={styles.feedbackRow}>
        {!hasError && helpMessage && (
          <Hint id={hintId} className={clsx('c2 book', 'primary-text-color-submain-normal')}>
            {helpMessage}
          </Hint>
        )}
        {hasError && (
          <Error id={errorId} className={clsx('c2 book', 'text-color-destructive')}>
            {validationMessage}
          </Error>
        )}
      </div>
    </div>
  );
}
